import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { FaPlus, FaEdit, FaEye } from 'react-icons/fa';
import NoRecordFound from '../../../components/common/no-record-found';
import ViewModal from '../../../components/common/ViewModal';

const ROWS_PER_PAGE = 25;

const PurchaseContractList = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);
    return () => clearTimeout(handler);
  }, [search]);

  const fetchRecords = async (page, searchValue) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
        search: searchValue || '',
      });
      const res = await fetch(`${API_BASE_URL}/api/contracts/purchase-contract-get?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to fetch');
              const result = await res.json();
        
        // Debug log removed
        setRecords(result.data || []);
        setTotal(result.total || 0);
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords(page, debouncedSearch);
    // eslint-disable-next-line
  }, [page, debouncedSearch]);

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatNumber = (number, decimals = 2) => {
    if (number === null || number === undefined || number === '') return '-';
    return parseFloat(number).toFixed(decimals);
  };

  const handleViewRecord = (record) => {
    console.log('Modal record data:', record);
    setSelectedRecord(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
  };
  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">Purchase Contracts</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search records..."
              value={search}
              onChange={handleSearchChange}
              className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-[12px] w-full sm:w-56 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>
          <button
            className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
            onClick={() => navigate('/purchase-contract/new')}
          >
            <FaPlus className="text-[10px]" />
            Add New
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-blue-600 py-6 text-[13px]">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-[13px]">{error}</div>
      ) : records.length === 0 ? (
        <NoRecordFound message="No purchase contract records found." />
      ) : (
        <div className="overflow-x-auto border border-gray-300 rounded-sm">
          <table className="min-w-full text-left border-collapse">
            <thead className="bg-[#f4f4f4] border-b border-gray-300">
              <tr>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Contract No</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Date</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Supplier</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Commodity</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Vessel Name</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Qty (MT)</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Unit Price</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Status</th>
                <th className="px-3 py-2 font-semibold text-gray-800 uppercase tracking-wider text-[11px] border-r border-gray-200 last:border-r-0">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {records.map((item, idx) => (
                <tr key={item.purchase_contract_no} className="hover:bg-blue-50/50 transition-colors duration-150 border-b border-gray-200 last:border-b-0">
                  <td className="px-3 py-1.5 font-medium text-gray-900 text-[12px] border-r border-gray-100 last:border-r-0">{item.purchase_contract_no}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{formatDate(item.contract_date)}</td>
                  <td className="px-3 py-1.5 text-gray-900 font-medium text-[12px] border-r border-gray-100 last:border-r-0">{item.supplier_name || '-'}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.commodity_name || '-'}</td>
                  <td className="px-3 py-1.5 text-gray-800 text-[12px] border-r border-gray-100 last:border-r-0">{item.vessel_name || '-'}</td>
                  <td className="px-3 py-1.5 font-bold text-gray-900 text-[12px] border-r border-gray-100 last:border-r-0">{formatNumber(item.total_qty_mt, 3)}</td>
                  <td className="px-3 py-1.5 font-semibold text-green-700 text-[12px] border-r border-gray-100 last:border-r-0">₹{formatNumber(item.unit_price_inr, 2)}</td>
                  <td className="px-3 py-1.5 border-r border-gray-100 last:border-r-0">
                    <div className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${
                        item.status === 'Open' ? 'bg-[#15803d]' : 
                        item.status === 'Closed' ? 'bg-gray-500' : 
                        item.status === 'Cancelled' ? 'bg-[#dc2626]' : 
                        'bg-[#2563eb]'
                      }`}></div>
                      <span className="text-[12px] font-medium text-gray-900">{item.status || 'Open'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1.5 border-r border-gray-100 last:border-r-0">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleViewRecord(item)}
                        className="text-gray-500 hover:text-blue-600 transition-colors"
                        title="View"
                      >
                        <FaEye className="text-[14px]" />
                      </button>
                      <button
                        onClick={() => {
                          console.log("Edit contract ID:", item.id);
                          navigate(`/purchase-contract/edit/${item.id}`);
                        }}
                        className="text-gray-500 hover:text-[#15803d] transition-colors"
                        title="Edit"
                      >
                        <FaEdit className="text-[14px]" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Totals Row */}
            <tfoot className="bg-[#f4f4f4] border-t border-gray-300">
              <tr>
                <td className="px-3 py-2 font-bold text-gray-900 text-[12px] text-right border-r border-gray-200" colSpan="5">
                  TOTAL:
                </td>
                <td className="px-3 py-2 font-bold text-gray-900 text-[12px] border-r border-gray-200">
                  {records.reduce((sum, record) => sum + (parseFloat(record.total_qty_mt) || 0), 0).toFixed(3)} MT
                </td>
                <td className="px-3 py-2" colSpan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

    

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-3 text-[12px]">
          <div className="text-gray-600 mb-2 sm:mb-0">
            Showing <span className="font-semibold text-gray-900">{((page - 1) * ROWS_PER_PAGE) + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(page * ROWS_PER_PAGE, total)}</span> of <span className="font-semibold text-gray-900">{total}</span> records
          </div>
          <div className="inline-flex shadow-sm rounded-sm">
            <button
              onClick={handlePrevPage}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-l-sm transition-colors"
            >
              Previous
            </button>
            <div className="px-3 py-1 border-t border-b border-gray-300 bg-gray-50 text-gray-900 font-medium">
              {page} / {totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-r-sm transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* View Modal */}
      <ViewModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={`Purchase Contract Details - ${selectedRecord?.purchase_contract_no || ''}`}
        size="xl"
      >
        {selectedRecord && (
          <div className="space-y-6">
            {/* Simple Clean Header */}
            <div className="bg-white px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-[20px] font-bold text-gray-800">
                  Contract Details: {selectedRecord.purchase_contract_no}
                </h2>
                <p className="text-[13px] text-gray-500 mt-1 font-medium">
                  Created on {formatDate(selectedRecord.contract_date)}
                </p>
              </div>
              <div>
                <span className={`px-3 py-1 rounded text-[12px] font-bold uppercase tracking-wide border ${
                  selectedRecord.status === 'Open' ? 'bg-green-100 text-green-800 border-green-200' : 
                  selectedRecord.status === 'Closed' ? 'bg-gray-100 text-gray-800 border-gray-200' : 
                  selectedRecord.status === 'Cancelled' ? 'bg-red-100 text-red-800 border-red-200' : 
                  'bg-white text-gray-800 border-gray-200'
                }`}>
                  Status: {selectedRecord.status || 'Open'}
                </span>
              </div>
            </div>

            {/* Complete Contract Details */}
            <div className="p-6 space-y-6 bg-gray-50 flex-1 overflow-y-auto">
              
              {/* Top Row: Parties & Core Details */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                
                {/* Contract Entities */}
                <div className="bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h3 className="text-[14px] font-bold text-gray-800 uppercase">Contract Entities</h3>
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="border-b border-gray-100 pb-3">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Supplier</span>
                      <p className="text-[15px] font-bold text-gray-900 mt-0.5">
                        {selectedRecord.supplier_name || '-'}
                      </p>
                    </div>
                    
                    {selectedRecord.contract_maker === 'By Broker' && (
                      <div className="grid grid-cols-2 gap-3 border-b border-gray-100 pb-3">
                        <div>
                          <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Broker</span>
                          <p className="text-[14px] font-bold text-gray-900 mt-0.5">{selectedRecord.broker_name || '-'}</p>
                        </div>
                        <div>
                          <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Broker Rate</span>
                          <p className="text-[14px] font-bold text-gray-900 mt-0.5">{selectedRecord.broker_rate ? `₹${selectedRecord.broker_rate}/MT` : '-'}</p>
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Contract Maker</span>
                      <p className="text-[14px] font-bold text-gray-900 mt-0.5">{selectedRecord.contract_maker || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Logistics Info */}
                <div className="bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h3 className="text-[14px] font-bold text-gray-800 uppercase">Logistics & Origin</h3>
                  </div>
                  <div className="p-4 grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Commodity</span>
                      <p className="text-[14px] font-bold text-gray-900">{selectedRecord.commodity_name || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Vessel Name</span>
                      <p className="text-[14px] font-bold text-gray-900">{selectedRecord.vessel_name || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">State of Purchase</span>
                      <p className="text-[14px] font-bold text-gray-900">{selectedRecord.state_of_purchase || '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">State Code</span>
                      <p className="text-[14px] font-bold text-gray-900">{selectedRecord.state_code || '-'}</p>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Port Name</span>
                      <p className="text-[14px] font-bold text-gray-900">{selectedRecord.port_name || '-'}</p>
                    </div>
                  </div>
                </div>

              </div>

              {/* Financials & Volume */}
              <div className="bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-[14px] font-bold text-gray-800 uppercase">Financials & Volume</h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="border border-gray-200 p-3 rounded-sm">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Total Qty (MT)</span>
                      <span className="text-[16px] font-bold text-gray-900">{formatNumber(selectedRecord.total_qty_mt, 3)}</span>
                    </div>
                    <div className="border border-gray-200 p-3 rounded-sm">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Unit Price (INR)</span>
                      <span className="text-[16px] font-bold text-gray-900">₹{formatNumber(selectedRecord.unit_price_inr, 2)}</span>
                    </div>
                    <div className="border border-gray-200 p-3 rounded-sm md:col-span-2">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide block mb-1">Total Contract Value</span>
                      <span className="text-[18px] font-bold text-gray-900">₹{formatNumber(selectedRecord.total_qty_mt * selectedRecord.unit_price_inr, 2)}</span>
                    </div>
                    
                    <div className="p-2">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Payment Term</span>
                      <p className="text-[14px] font-bold text-gray-900">{selectedRecord.payment_term || '-'}</p>
                    </div>
                    <div className="p-2">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Qty Tolerance</span>
                      <p className="text-[14px] font-bold text-gray-900">{formatNumber(selectedRecord.quantity_tolerance_pct, 2)}%</p>
                    </div>
                    <div className="p-2 md:col-span-2">
                      <span className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide">Markup</span>
                      <p className="text-[14px] font-bold text-gray-900">{formatNumber(selectedRecord.markup_pct, 2)}%</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remarks */}
              {selectedRecord.remarks && (
                <div className="bg-white border border-gray-200 rounded-sm shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <h3 className="text-[14px] font-bold text-gray-800 uppercase">Remarks</h3>
                  </div>
                  <div className="p-4">
                    <p className="text-[13px] text-gray-700 leading-relaxed font-medium">
                      {selectedRecord.remarks}
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Footer Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-3 py-1.5 text-[13px] font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Close
              </button>
              <button
                onClick={() => {
                  navigate(`/purchase-contract/edit/${selectedRecord.id}`);
                  closeModal();
                }}
                className="px-3 py-1.5 text-[13px] font-medium text-white bg-gray-600 border border-transparent rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Edit Contract
              </button>
            </div>
          </div>
        )}
      </ViewModal>
    </div>
  );
};

export default PurchaseContractList;
